import React, {useMemo, useState} from 'react';
import { isIoTAdminToken, isIotAttachEntryVisible } from '../../utils/iotAttachGate';
import {Navigate, Route, Routes, useMatch, useParams} from "react-router-dom";
import AssetDetailNavigation from "../../components/AssetDetailNavigation";
import {Box, Button} from "@mui/material";
import AssetDashboard from "../../components/AssetDashboard";
import AssetSpecifications from "../../components/AssetSpecifications";
import AssetFileManagement from "../../components/AssetFileManagement";
import AssetEventList from "../../components/AssetEventList";
import AssetContaminationDeclarations from "../../components/AssetContaminationDeclarations";
import {isContaminationDeclarationsEnabled} from "../../utils/contaminationDeclarationsEnabled";
import AssetIoTTelemetry from "../../components/AssetIoTTelemetry";
import AssetConnectionWizard from "../../components/AssetConnectionWizard";
import AssetConditionMonitoringIframe from "../../components/AssetConditionMonitoringIframe";
import AssetServiceReminders from "../../components/AssetServiceReminders";
import {useQuery} from "@apollo/client";
import {AssetNodeType} from "../../graphql/queries/assetNodeType";
import {ServiceReminderList} from "../../graphql/queries/serviceReminderList";
import {AssetIotConnectionId} from "../../graphql/queries/assetIotConnectionId";
import {AssetBreadcrumbPath} from "../../graphql/queries/assetBreadcrumbPath";
import {AssetLocationClientContract} from "../../graphql/queries/assetLocationClientContract";
import {isContractValid, isContractExpired, canViewIoT} from "../../utils/isContractValid";
import {IndustryType} from "../../hooks/useIndustryType";
import _ from "lodash";
import useMediaQuery from "@mui/system/useMediaQuery";
import {Typography, useTheme} from "@mui/joy";
import AssetDashboardActions from "../../components/AssetDashboardActions";
import Breadcrumbs, {breadcrumbConfig, BreadcrumbMatch} from "../../components/Breadcrumbs";
import useBreadcrumbs from "use-react-router-breadcrumbs";
import {useTranslation} from "react-i18next";
import AssetEventDetailView from "../../components/AssetEventDetailView";
import UpdateInstalledBaseEventModal from "../../components/UpdateInstalledBaseEventModal";
import {EventNavigation} from "../../components/AssetEventList/table";
import {useSelectedEventParam} from "../../hooks/useSelectedEventParam";
import AssetAlertList from "../../components/AssetAlertList";
import LegacyRouteRedirect from "../../components/LegacyRouteRedirect";
import {useIoTConnectionPolling} from "../../hooks/useIoTConnectionPolling";
import {useCompactWidth} from "../../hooks/useCompactWidth";
import {useKeycloak} from "@react-keycloak/web";
import {getBreadcrumbPathNodeLink} from "../../utils/getBreadcrumbPathNodeLink";
import {useAssetSlaStatus} from "../../hooks/useAssetSlaStatus";
import {useAssetContaminationDeclarationsVisible} from "../../hooks/useAssetContaminationDeclarationsVisible";


const AssetDetailsScreen = () => {
    const { assetLocationId } = useParams();
    const theme = useTheme();
    const {t} = useTranslation();
    const { keycloak } = useKeycloak();

    const isIoTAdmin = useMemo(() => isIoTAdminToken(keycloak.token), [keycloak.token]);
    const rawBreadcrumbs = useBreadcrumbs(breadcrumbConfig(t));

    const [selectedEventId, setSelectedEventId] = useSelectedEventParam();
    const [eventNavigation, setEventNavigation] = useState<EventNavigation | null>(null);
    const [editEventId, setEditEventId] = useState<string | number | null>(null);
    // The event panel belongs to the event history; other tabs ignore a stray ?event=
    const isEventsRoute = !!useMatch('/assets/:assetLocationId/events');

    const assetPathQuery = useQuery(AssetBreadcrumbPath, {
        variables: { id: assetLocationId },
        skip: !assetLocationId,
    });

    const breadcrumbs = useMemo(() => {
        const assetNode = _.get(assetPathQuery.data, 'assetLocation.installedBaseNode');
        const pathNodes = assetNode?.pathNodes;
        if (!pathNodes?.length || pathNodes.length <= 1) return rawBreadcrumbs;

        // Insert parent path nodes (all except the last) before the asset crumb
        const expanded: BreadcrumbMatch[] = [];
        for (const crumb of rawBreadcrumbs) {
            if (crumb.match.pathname === `/assets/${assetLocationId}`) {
                // Add parent path nodes as clickable breadcrumb items (asset/system ancestors
                // open their dashboard, all others the installed base tree)
                pathNodes.slice(0, -1).forEach((node: any, i: number) => {
                    const pathIds = pathNodes.slice(0, i + 1).map((n: any) => n.id);
                    expanded.push({
                        match: crumb.match,
                        breadcrumb: node.title,
                        key: `pathnode-${node.id}`,
                        linkTo: getBreadcrumbPathNodeLink(node, pathIds),
                    });
                });
            }
            expanded.push(crumb);
        }
        return expanded;
    }, [rawBreadcrumbs, assetPathQuery.data, assetLocationId]);

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showNavigation, setShowNavigation] = React.useState(false);
    const { ref: contentRef, compact } = useCompactWidth();

    const handleSwapView = () => {
        setShowNavigation(prev => !prev);
    }

    const assetNodeTypeQuery = useQuery(AssetNodeType, {
        variables: {
            id: assetLocationId
        },
        skip: !assetLocationId
    })

    const assetIotConnectionQuery = useQuery(AssetIotConnectionId, {
        variables: {
            id: assetLocationId
        },
        skip: !assetLocationId,
        fetchPolicy: "network-only",
    })

    const assetLocationClientContractQuery = useQuery(AssetLocationClientContract, {
        variables: { id: assetLocationId },
        skip: !assetLocationId
    });
    const contractData = _.get(assetLocationClientContractQuery, 'data.assetLocation.owner.contract');
    const contractValid = isContractValid(contractData);
    const contractExpired = isContractExpired(contractData);
    // IoT admins may view existing data for an expired contract.
    const canMonitorIoT = canViewIoT(contractData, isIoTAdmin);
    const isSemiconductor = _.get(assetLocationClientContractQuery, 'data.assetLocation.owner.industryType') === IndustryType.SEMICONDUCTOR;

    const isInstalled = !!_.get(assetNodeTypeQuery, "data.assetLocation.installedBaseNode.id");
    const hasIotConnection = !!_.get(assetIotConnectionQuery, "data.assetLocation.iotConnection.id");

    const installedBaseNodeId = _.get(assetNodeTypeQuery, 'data.assetLocation.installedBaseNode.id');

    const serviceReminderQuery = useQuery(ServiceReminderList, {
        variables: { installedBaseNodeId },
        skip: !installedBaseNodeId,
    });
    const hasServiceReminders = (_.get(serviceReminderQuery, 'data.serviceReminderList.total', 0) ?? 0) > 0;

    const { isPending: isPendingIotConnection, markAsPending: markIoTConnectionPending } = useIoTConnectionPolling({
        assetLocationId: assetLocationId ?? "",
        hasIotConnection,
        startPolling: assetIotConnectionQuery.startPolling,
        stopPolling: assetIotConnectionQuery.stopPolling,
    });

    // SLA status (IBM-1045): IoT admins of a live, non-semiconductor device only — the
    // same rule as the dashboard hero chip. Gates the "SLA Report" entry and route.
    const assetIdForSla = _.get(assetNodeTypeQuery, 'data.assetLocation.asset.id');
    const sla = useAssetSlaStatus({
        assetLocationId,
        assetId: assetIdForSla,
        enabled: hasIotConnection && !isSemiconductor && isIoTAdmin,
    });
    const hasSlaStatus = isIoTAdmin && !!sla.status;

    // DoC nav entry: only when the newest removal has a declaration or may start one.
    const showContaminationDeclarations = useAssetContaminationDeclarationsVisible(assetIdForSla);

    if (!assetLocationId) return null;

    // IoT data routes are only reachable while the contract permits monitoring.
    const guardIoT = (element: React.ReactElement) =>
        assetLocationClientContractQuery.loading ? null :
        canMonitorIoT ? element :
        <Navigate to={`/assets/${assetLocationId}/dashboard`} replace />;

    const nodeTypeKey = _.get(assetNodeTypeQuery, 'data.assetLocation.installedBaseNode.nodeType.key')
    const assetId = _.get(assetNodeTypeQuery, 'data.assetLocation.asset.id')
    const serialNumber = _.get(assetNodeTypeQuery, 'data.assetLocation.asset.serialNumber')

    return (
        <div className="flex-row" style={{ flexGrow: 1}}>
            <Box
                sx={{
                    width: 320,
                    borderRight: "1px solid #ccc",
                    margin: "12px 0",
                    padding: "0 12px",
                    display: isMobile ? (showNavigation ? 'flex' : 'none') : 'flex',
                    flexDirection: 'column'
                }}
            >
                <AssetDetailNavigation
                    assetLocationId={assetLocationId}
                    installedBaseNodeId={installedBaseNodeId}
                    installedBaseNodeTypeKey={nodeTypeKey}
                    isInstalled={isInstalled}
                    hasIotConnection={hasIotConnection}
                    isPendingIotConnection={isPendingIotConnection}
                    isContractValid={contractValid}
                    isContractExpired={contractExpired}
                    isIoTAdmin={isIoTAdmin}
                    hasServiceReminders={hasServiceReminders}
                    isSemiconductor={isSemiconductor}
                    hasSlaStatus={hasSlaStatus}
                    showContaminationDeclarations={showContaminationDeclarations}
                />

                {isMobile && (
                    <Button onClick={handleSwapView} variant="outlined" sx={{ mb: 2 }}>
                        <Typography level="body-md">
                            {showNavigation ? "Show Content" : "Show Navigation"}
                        </Typography>
                    </Button>
                )}
            </Box>

            <Box
                sx={{
                    flex: 3,
                    padding: "12px 0 12px 12px",
                    minWidth:'0',
                    display: isMobile ? (showNavigation ? 'none' : 'flex') : 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{pb: 2}}
                >
                    <Breadcrumbs breadcrumbs={breadcrumbs} sx={{ flex: 1, minWidth: 0 }} />
                    <AssetDashboardActions
                        assetLocationId={assetLocationId}
                        installedBaseNodeId={installedBaseNodeId}
                    />
                </Box>

                {isMobile && (
                    <Button onClick={handleSwapView} variant="outlined" sx={{ mb: 2 }}>
                        <Typography level="body-md">
                            {showNavigation ? "Show Content" : "Show Navigation"}
                        </Typography>
                    </Button>
                )}

                <Box ref={contentRef} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <Routes>
                        <Route
                            path="dashboard"
                            element={<AssetDashboard
                                assetLocationId={assetLocationId}
                                installedBaseNodeId={installedBaseNodeId}
                                hasIotConnection={hasIotConnection}
                                isPendingIotConnection={isPendingIotConnection}
                                isIoTAdmin={isIoTAdmin}
                                serialNumber={serialNumber}
                                hasServiceReminders={hasServiceReminders}
                                compact={compact}
                            />}
                        />
                        <Route path="specifications" element={<AssetSpecifications assetLocationId={assetLocationId} />} />
                        <Route path="files" element={<AssetFileManagement assetLocationId={assetLocationId} installedBaseNodeId={installedBaseNodeId} isLoadingInstalledBaseNode={assetNodeTypeQuery.loading}/>} />
                        <Route path="iot" element={guardIoT(<AssetIoTTelemetry assetLocationId={assetLocationId} installedBaseNodeId={installedBaseNodeId}/>)} />
                        <Route path="events" element={<AssetEventList
                            assetId={assetId}
                            assetLocationId={assetLocationId}
                            installedBaseNodeId={installedBaseNodeId}
                            onOpenEventDetailView={setSelectedEventId}
                            onEditEvent={(id) => setEditEventId(id)}
                            selectedEventId={selectedEventId}
                            onNavigationChange={setEventNavigation}
                        />} />
                        {isContaminationDeclarationsEnabled() && (
                            <Route path="contaminationDeclarations" element={<AssetContaminationDeclarations assetId={assetId}/>} />
                        )}
                        <Route
                            path="connect"
                            element={
                                assetNodeTypeQuery.loading || assetIotConnectionQuery.loading || assetLocationClientContractQuery.loading ? null :
                                // Frontend rule shared with every connect entry point
                                // (src/utils/iotAttachGate.ts); the network scan gate is
                                // checked inside the wizard, which explains a blocked scan.
                                !isIotAttachEntryVisible({ isIoTAdmin, isInstalled, hasIotConnection, isPendingIotConnection, contractValid, isSemiconductor }) ? (
                                    <Navigate to={`/assets/${assetLocationId}/dashboard`} replace />
                                ) : (
                                    <AssetConnectionWizard assetLocationId={assetLocationId} installedBaseNodeId={installedBaseNodeId} onIoTConnectionCreated={markIoTConnectionPending} />
                                )
                            }
                        />
                        <Route
                            path="connect/edit"
                            element={
                                assetNodeTypeQuery.loading || assetIotConnectionQuery.loading ? null :
                                // Unlike create, editing an existing connection is not gated
                                // by contract validity or industry type.
                                !isIoTAdmin || !isInstalled || !hasIotConnection ? (
                                    <Navigate to={`/assets/${assetLocationId}/dashboard`} replace />
                                ) : (
                                    <AssetConnectionWizard assetLocationId={assetLocationId} installedBaseNodeId={installedBaseNodeId} mode="edit" />
                                )
                            }
                        />
                        <Route path="serviceReminders" element={<AssetServiceReminders assetLocationId={assetLocationId} installedBaseNodeId={installedBaseNodeId} />} />
                        <Route path="conditionMonitoring/telemetry" element={guardIoT(<AssetConditionMonitoringIframe assetLocationId={assetLocationId} page="telemetry" />)} />
                        <Route path="conditionMonitoring/maintenance" element={guardIoT(<AssetConditionMonitoringIframe assetLocationId={assetLocationId} page="maintenance" />)} />
                        <Route
                            path="conditionMonitoring/slaReport"
                            element={
                                assetIotConnectionQuery.loading || assetLocationClientContractQuery.loading || !sla.settled ? null :
                                hasSlaStatus ? guardIoT(<AssetConditionMonitoringIframe assetLocationId={assetLocationId} page="sla-asset-report" />) :
                                <Navigate to={`/assets/${assetLocationId}/dashboard`} replace />
                            }
                        />
                        <Route path="conditionMonitoring/alerts" element={guardIoT(<AssetAlertList assetLocationId={assetLocationId} />)} />
                        {/* Renamed to camelCase; IoT alert notifications still link to the old path */}
                        <Route path="condition-monitoring/*" element={<LegacyRouteRedirect />} />
                        <Route path="*" element={<Navigate to={`/assets/${assetLocationId}/dashboard`} replace />} />
                    </Routes>
                </Box>

            </Box>


                {
                    isEventsRoute && selectedEventId &&
                                <AssetEventDetailView
                                    eventId={selectedEventId}
                                    onCloseEventDetailView={() => setSelectedEventId(null)}
                                    onEditEvent={(id) => setEditEventId(id)}
                                    navigation={eventNavigation}
                                />

                }

            {editEventId && (
                <UpdateInstalledBaseEventModal
                    eventId={String(editEventId)}
                    open={!!editEventId}
                    onClose={() => setEditEventId(null)}
                />
            )}
        </div>
    );
};

export default AssetDetailsScreen;
